import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Building2, LogOut, Sun, Moon, Home, Wallet, Receipt, MessageSquare, User, Building, FileCheck, Megaphone } from "lucide-react";
import PunjabEmblem from "@/components/PunjabEmblem";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const CITIZEN_NAV = [
  { to: "/portal", label: "Dashboard", icon: Home, end: true },
  { to: "/portal/pay", label: "Pay Dues", icon: Wallet },
  { to: "/portal/receipts", label: "Receipts", icon: Receipt },
  { to: "/portal/licenses", label: "Trade Licenses", icon: FileCheck },
  { to: "/portal/advertisements", label: "Advertisements", icon: Megaphone },
  { to: "/portal/building-plans", label: "Building Plans", icon: Building },
  { to: "/portal/complaints", label: "Complaints", icon: MessageSquare },
  { to: "/portal/profile", label: "Profile", icon: User },
];

export default function CitizenLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [dark, setDark] = React.useState(() => document.documentElement.classList.contains("dark"));
  const toggleDark = () => {
    const n = !dark;
    setDark(n);
    document.documentElement.classList.toggle("dark", n);
    localStorage.setItem("pulb_theme", n ? "dark" : "light");
  };
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="tricolor-bar" />
      <header className="sticky top-0 z-30 bg-white/85 dark:bg-slate-900/85 backdrop-blur-md border-b border-slate-200/70 dark:border-slate-800" data-testid="citizen-topbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/portal" className="flex items-center gap-3" data-testid="link-portal-home">
            <PunjabEmblem size={40} />
            <div>
              <div className="font-heading font-semibold text-sm text-slate-900 dark:text-slate-100">PULB DRMS</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                Citizen Portal
              </div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {CITIZEN_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`citizen-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 text-sm rounded-md font-medium transition-colors ${
                    isActive ? "bg-[#0F2942] text-white" : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`
                }
              >
                <n.icon className="w-4 h-4" strokeWidth={1.75} />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleDark} data-testid="btn-toggle-theme-citizen">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-2 pr-3" data-testid="btn-citizen-menu">
                  <Avatar className="w-7 h-7">
                    <AvatarFallback className="bg-[#E8702A] text-white text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm">{user?.name?.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="citizen-menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Mobile nav */}
        <nav className="md:hidden overflow-x-auto flex gap-1 px-3 pb-2 border-t border-slate-100 dark:border-slate-800">
          {CITIZEN_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `whitespace-nowrap text-xs px-3 py-1.5 rounded-md ${isActive ? "bg-[#0F2942] text-white" : "text-slate-600"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 dark:border-slate-800 py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} PULB Digital Revenue Management System
      </footer>
    </div>
  );
}
